/**
 * classes.ts — Player class definitions for Gallowcrest.
 *
 * Defines all 5 playable classes with starting stats, HP, frostbite costs,
 * and backstory introductions used during character creation.
 */

import type { ClassId, StatName } from './types';

/** A playable character class definition. */
export interface PlayerClass {
  id: ClassId;
  name: string;
  /** Short tagline for menu display. */
  tagline: string;
  description: string;
  /** Unicode symbol for display. */
  symbol: string;
  /** Display color name (matches ColorName). */
  color: string;
  primaryStat: StatName;
  startingHP: number;
  /** Frostbite cost for class combat ability. */
  frostbiteCost: number;
  startingStats: Record<StatName, number>;
  backstoryIntro: string;
}

/**
 * All 5 class definitions.
 *
 * Starting stats: 14 in primary, 12 in secondary (thematically related),
 * 10 in the remaining four stats.
 */
export const classes: Record<ClassId, PlayerClass> = {
  frostblade_fighter: {
    id: 'frostblade_fighter',
    name: 'Frostblade Fighter',
    tagline: 'Winter-forged warriors',
    symbol: '\u2694',
    color: 'red',
    description:
      'Warriors who channel winter\'s power through enchanted weapons. Masters of combat in freezing conditions.',
    primaryStat: 'strength',
    startingHP: 12,
    frostbiteCost: 5,
    startingStats: {
      strength: 14,
      constitution: 12,
      dexterity: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10,
    },
    backstoryIntro:
      'You are a veteran of the Winterguard, trained to fight in the harshest conditions the north can offer. Your blade carries the bite of winter itself, and where others falter against the cold, you thrive. The call to arms came as no surprise — you\'ve felt the unnatural chill growing for weeks, and you know what it means. The dead are rising, and the living need a sword arm they can trust.',
  },

  necromancer_hunter: {
    id: 'necromancer_hunter',
    name: 'Necromancer Hunter',
    tagline: 'Bane of the undead',
    symbol: '\u2740',
    color: 'green',
    description:
      'Specialists trained to destroy undead and resist necromantic magic. The bane of the Lich King\'s forces.',
    primaryStat: 'dexterity',
    startingHP: 10,
    frostbiteCost: 3,
    startingStats: {
      dexterity: 14,
      wisdom: 12,
      strength: 10,
      constitution: 10,
      intelligence: 10,
      charisma: 10,
    },
    backstoryIntro:
      'You hunt the dead — and those who raise them. Years of training have honed your senses to detect the faintest whiff of necromancy, and your strikes are precise enough to sever the bonds that animate corpses. You\'ve tracked the growing darkness north for months, cataloguing signs that others dismissed as superstition. Now the Winterguard Council has finally listened. The dead walk, and you were born for this war.',
  },

  dragon_knight: {
    id: 'dragon_knight',
    name: 'Dragon Knight',
    tagline: 'Dragonscale armor, unyielding',
    symbol: '\u25C6',
    color: 'yellow',
    description:
      'Fighters wearing armor forged from dragon scales, resistant to dragonfire and trained in anti-dragon combat.',
    primaryStat: 'strength',
    startingHP: 14,
    frostbiteCost: 7,
    startingStats: {
      strength: 14,
      constitution: 12,
      dexterity: 10,
      intelligence: 10,
      wisdom: 10,
      charisma: 10,
    },
    backstoryIntro:
      'Your armor is ancient — forged from the scales of Kharnathex the Eternal, the last true dragon to fall in battle against mortal hands. The order of Dragon Knights has dwindled to a handful, but the blood of dragons still resonates in your veins. You feel the corruption spreading through the land like a sickness in your own body. Something vast and terrible is stirring in the frozen north, and your draconic senses scream that it must be stopped.',
  },

  winter_witch: {
    id: 'winter_witch',
    name: 'Winter Witch',
    tagline: 'Masters of ice magic',
    symbol: '\u2726',
    color: 'cyan',
    description:
      'Mages who weaponize the eternal winter against the undead. Masters of ice and frost magic.',
    primaryStat: 'intelligence',
    startingHP: 8,
    frostbiteCost: 2,
    startingStats: {
      intelligence: 14,
      charisma: 12,
      strength: 10,
      dexterity: 10,
      constitution: 10,
      wisdom: 10,
    },
    backstoryIntro:
      'While others fear the winter, you command it. Your mastery of ice magic makes you uniquely suited to fight in the frozen wastes — the cold is your weapon, not your enemy. You\'ve studied the arcane anomalies spreading from the north, and your analysis is grim: someone is channeling the ley lines themselves, warping winter into something far more dangerous than any natural storm. The power required would be staggering. You must see this for yourself.',
  },

  runepriest: {
    id: 'runepriest',
    name: 'Runepriest',
    tagline: 'Sacred wards of light',
    symbol: '\u2726',
    color: 'magenta',
    description:
      'Divine casters who ward against undeath using ancient symbols. Bearers of sacred light in the frozen darkness.',
    primaryStat: 'wisdom',
    startingHP: 10,
    frostbiteCost: 4,
    startingStats: {
      wisdom: 14,
      charisma: 12,
      strength: 10,
      dexterity: 10,
      constitution: 10,
      intelligence: 10,
    },
    backstoryIntro:
      'The ancient runes speak to you in ways they haven\'t spoken to anyone in generations. Where the dead rise, your sacred symbols burn with divine fire. Where corruption spreads, your wards hold the darkness at bay. But lately, the runes have gone quiet — as if the divine presence that empowers them is withdrawing. The silence frightens you more than any undead horde. You travel north to discover why the gods have turned their faces from this land.',
  },
};

/** Get a class definition by ID. */
export function getClass(classId: ClassId): PlayerClass {
  return classes[classId];
}

/** Get all class IDs. */
export function getClassIds(): ClassId[] {
  return Object.keys(classes) as ClassId[];
}
